import { useNavigate } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Atom, BookOpenText, Sigma, Globe2, ArrowRight, Tag } from "lucide-react";

const SUBJECTS = [
  {
    icon: Atom, color: "text-primary bg-primary/10", framework: "NGSS",
    title: "Science",
    blurb: "Next Generation Science Standards, built on the Framework for K-12 Science Education (National Academies, 2012).",
    bands: [
      { band: "Grades K-5", codes: ["K-PS2-1", "2-LS4-1", "3-LS1-1", "5-ESS1-1", "5-PS1-1"] },
      { band: "Grades 6-8", codes: ["MS-ESS1-1", "MS-ESS1-2", "MS-LS1-2", "MS-PS1-1", "MS-PS2-2"] },
      { band: "Grades 9-12", codes: ["HS-ESS1-4", "HS-LS1-2", "HS-PS1-3", "HS-PS4-1"] },
    ],
  },
  {
    icon: BookOpenText, color: "text-accent bg-accent/10", framework: "CCSS ELA/Literacy",
    title: "English Language Arts",
    blurb: "Common Core ELA/Literacy RST & WHST strands — the same literacy practices your English classroom teaches, rehearsed inside every scene.",
    bands: [
      { band: "Reading (Science/Tech)", codes: ["RST.6-8.1", "RST.6-8.7", "RST.9-10.7"] },
      { band: "Writing (Science/Tech)", codes: ["WHST.6-8.1", "WHST.6-8.9", "WHST.9-10.2"] },
      { band: "Speaking & Listening", codes: ["SL.5.5", "SL.8.5", "SL.11-12.5"] },
    ],
  },
  {
    icon: Sigma, color: "text-emerald-600 bg-emerald-500/10", framework: "CCSS Math",
    title: "Mathematics",
    blurb: "Common Core Math content and practice standards, tagged to the exact expectation a scene reinforces.",
    bands: [
      { band: "Grades 3-5", codes: ["3.NF.A.1", "4.MD.A.3", "5.G.A.2"] },
      { band: "Grades 6-8", codes: ["6.RP.A.3", "7.G.B.4", "8.G.A.1"] },
      { band: "High School", codes: ["HSG.MG.A.1", "HSF.IF.B.4"] },
    ],
  },
  {
    icon: Globe2, color: "text-cyan-600 bg-cyan-500/10", framework: "C3 / State Social Studies",
    title: "History & Geography",
    blurb: "C3 Framework and state social-studies standards for place-based virtual tours and immersive experiences.",
    bands: [
      { band: "Grades K-5", codes: ["D2.Geo.1.K-2", "D2.His.1.3-5"] },
      { band: "Grades 6-8", codes: ["D2.His.3.6-8", "D2.Geo.2.6-8"] },
      { band: "Grades 9-12", codes: ["D2.His.14.9-12", "D2.Civ.5.9-12"] },
    ],
  },
];

export default function Standards() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="max-w-3xl animate-in-up">
          <div className="caption-uppercase text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Curriculum Research Brief</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
            Standards VividVR's VR lessons cover
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Every scene VividVR generates is tagged with the specific Next Generation Science Standards and Common Core
            State Standards it addresses — built on the <em>Framework for K-12 Science Education</em> (National Academies,
            2012) and made concrete by the NGSS Lead States (NSTA, 2013). The Framework explicitly pairs NGSS Performance
            Expectations with CCSS ELA/Literacy RST and WHST strands, so the literacy practices your English classroom
            teaches are the ones students rehearse inside every science scene.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">Framework for K-12 Science Education (2012)</Badge>
            <Badge variant="secondary" className="text-xs">NGSS Lead States (NSTA, 2013)</Badge>
            <Badge variant="secondary" className="text-xs">Linked to gallery scenes where available</Badge>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {SUBJECTS.map((s) => (
            <div key={s.title} className="rounded-3xl border border-border bg-card p-7 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}><s.icon className="w-6 h-6" /></div>
                <Badge className="bg-primary/10 text-primary border-0 font-mono text-[10px]">{s.framework}</Badge>
              </div>
              <h3 className="text-xl font-display font-semibold mt-5">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.blurb}</p>
              <div className="mt-4 space-y-3">
                {s.bands.map((b) => (
                  <div key={b.band}>
                    <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{b.band}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {b.codes.map((c) => (
                        <span key={c} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-[11px] font-mono font-semibold">
                          <Tag className="w-3 h-3 opacity-60" /> {c}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-800 text-white p-10 sm:p-14 mt-14 text-center">
          <h2 className="text-2xl sm:text-3xl font-display font-bold tracking-tight">Want the full standards breakdown for your district?</h2>
          <p className="mt-3 text-white/80 max-w-2xl mx-auto">
            Generate a sample lesson against your pacing guide and we'll show you the exact tag set — or talk to us about a district-level standards report.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button size="lg" data-testid="standards-generator-btn" onClick={() => nav("/register")} className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100">
              Try the Generator <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" data-testid="standards-pricing-btn" onClick={() => nav("/pricing")} className="rounded-full h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10">
              See Pricing
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
